import isEmpty from "is-empty";
import PropTypes from "prop-types";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { Maybe, Product } from "rsl-api-106/lib/types";

import ReactComment from "@components/_Layout/ReactComment";
import { useCart, useLoadingScreen } from "@hooks";
import { getCurrencySymbol } from "@utils/currencies";

import ProductTooltip from "../ProductTooltip";

type SubProductsProps = {
  originalLabel: string;
  mode: "occupant" | "stay" | "global";
  products: Maybe<Maybe<Product>[]>;
  isInLineItem: (id: number) => { checked: boolean; quantity: number };
  occupantGuiid?: number;
  stayGuiid?: number;
  addLoadingText: string;
  addedSuccessText: string;
  removeLoadingText: string;
  removedSuccessText: string;
};
const SubProducts: React.FC<SubProductsProps> = ({
  originalLabel,
  mode,
  products,
  isInLineItem,
  occupantGuiid,
  stayGuiid,
  addLoadingText,
  removeLoadingText,
  addedSuccessText,
  removedSuccessText,
}) => {
  const { addProductToOccupant, addProductToGlobal } = useCart();
  /**
   * Given a list of products, return true if the product is in the cart
   * @returns A boolean value.
   */
  const isCheckboxChecked = () => {
    let checked = false;
    products?.forEach((product) => {
      if (!checked) {
        checked = isInLineItem(product?.id!!).checked;
      }
    });
    return checked;
  };
  const [checkboxChecked, setCheckboxChecked] = useState<boolean>(
    isCheckboxChecked()
  );
  const [isLoadingAddRemove, setIsLoadingAddRemove] = useState<boolean>(false);
  const [loadingTitle, setLoadingTitle] = useState<string>("");
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let quantity = e.target.checked ? 1 : 0;
    setLoadingTitle(quantity === 0 ? removeLoadingText : addLoadingText);
    setIsLoadingAddRemove(true);
    if (products) {
      if (mode === "occupant" && occupantGuiid) {
        addProductToOccupant(
          products[0]?.code!!,
          products[0]?.id!!,
          occupantGuiid,
          0,
          (err) => {
            if (!err) {
              addProductToOccupant(
                products[0]?.code!!,
                products[0]?.id!!,
                occupantGuiid,
                quantity,
                (e) => {
                  if (e) toast.error(e.message);
                  else {
                    toast.success(
                      quantity === 0 ? removedSuccessText : addedSuccessText
                    );
                    setCheckboxChecked(quantity === 0 ? false : true);
                  }
                  setIsLoadingAddRemove(false);
                }
              );
            }
          }
        );
      }
      if (mode === "stay" && stayGuiid) {
        addProductToGlobal(
          products[0]?.code!!,
          products[0]?.id!!,
          0,
          (err) => {
            if (!err) {
              addProductToGlobal(
                products[0]?.code!!,
                products[0]?.id!!,
                quantity,
                (e) => {
                  if (e) toast.error(e.message);
                  else {
                    toast.success(
                      quantity === 0 ? removedSuccessText : addedSuccessText
                    );
                    setCheckboxChecked(quantity === 0 ? false : true);
                  }
                  setIsLoadingAddRemove(false);
                },
                stayGuiid
              );
            }
          },
          stayGuiid
        );
      }
    }
  };
  const handleRadioChange = (productCode: string, productId: number) => {
    //si checked === true, on ajouter "supprime" le produit de la liste avec quantité 0
    //si checked === false, on ajoute le produit avec quantité 1
    let quantity = isInLineItem(productId).quantity;
    const checked = quantity > 0 ? true : false;
    let qte = checked ? 0 : 1;
    setLoadingTitle(checked ? removeLoadingText : addLoadingText);
    setIsLoadingAddRemove(true);
    if (mode === "occupant" && occupantGuiid) {
      addProductToOccupant(productCode, productId, occupantGuiid, 0, (err) => {
        if (!err) {
          addProductToOccupant(
            productCode,
            productId,
            occupantGuiid,
            qte,
            (e) => {
              if (e) toast.error(e.message);
              else {
                toast.success(
                  qte === 0 ? removedSuccessText : addedSuccessText
                );
                setCheckboxChecked(qte === 0 ? false : true);
              }
              setIsLoadingAddRemove(false);
            }
          );
        }
      });
    }
    if (mode === "stay" && stayGuiid) {
      addProductToGlobal(
        productCode,
        productId,
        0,
        (err) => {
          if (!err) {
            addProductToGlobal(
              productCode,
              productId,
              qte,
              (e) => {
                if (e) toast.error(e.message);
                else {
                  toast.success(
                    qte === 0 ? removedSuccessText : addedSuccessText
                  );
                  setCheckboxChecked(qte === 0 ? false : true);
                }
                setIsLoadingAddRemove(false);
              },
              stayGuiid
            );
          }
        },
        stayGuiid
      );
    }
  };
  useLoadingScreen(isLoadingAddRemove, loadingTitle);
  return (
    <div>
      <div className="tw-flex tw-items-center tw-gap-y-2 tw-gap-x-4 tw-text-lg">
        <input
          type="checkbox"
          className="tw-accent-primary-500 hover:tw-accent-primary-400 tw-h-4 tw-w-4"
          onChange={(e) => handleCheckboxChange(e)}
          checked={checkboxChecked}
        ></input>
        <label>{originalLabel}</label>
      </div>
      <div>
        {products &&
          products.map((product, productIndex) => {
            return (
              product && (
                <React.Fragment
                  key={`subproduct_product_${product.code}_${productIndex}`}
                >
                  <ReactComment text={`product: ${JSON.stringify(product)} `} />
                  <div
                    key={`subproducts_${product.id}_${productIndex}`}
                    className="tw-flex tw-items-center tw-justify-between tw-gap-x-2 tw-pl-4"
                  >
                    <div className="tw-flex tw-items-center tw-gap-y-2 tw-gap-x-4 tw-text-lg">
                      <input
                        key={`product_${productIndex}`}
                        type="radio"
                        value={product?.id}
                        id={`input_subproduct_${product.id}_${productIndex}`}
                        onChange={(e) =>
                          handleRadioChange(product.code, product.id)
                        }
                        checked={isInLineItem(product.id).checked}
                        name={originalLabel}
                        className="tw-accent-primary-500 hover:tw-accent-primary-400 tw-h-4 tw-w-4"
                      />
                      <div>
                        <label
                          htmlFor={`input_subproduct_${product.id}_${productIndex}`}
                          className="form-check-label tw-inline-block"
                        >
                          {!isEmpty(product.webLabel)
                            ? product.webLabel
                            : product.label}
                        </label>
                        <ProductTooltip
                          description={product.description!!}
                          isProductMandatory={product.mandatory ?? false}
                        />
                      </div>
                    </div>
                    <div className="tw-flex tw-items-center">
                      {product.price?.amount !==
                      product.priceWithoutDiscounts?.amount ? (
                        <div>
                          <span className="tw-text-primary-500 tw-mr-2">
                            {product?.price?.amount?.toFixed(2)}
                            {getCurrencySymbol(product?.price?.currencyCode!!)}
                          </span>
                          <span className="tw-line-through">
                            {product?.priceWithoutDiscounts?.amount?.toFixed(2)}
                            {getCurrencySymbol(
                              product?.priceWithoutDiscounts?.currencyCode!!
                            )}
                          </span>
                        </div>
                      ) : (
                        <div>
                          <span>
                            {product?.price?.amount?.toFixed(2)}
                            {getCurrencySymbol(product?.price?.currencyCode!!)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              )
            );
          })}
      </div>
    </div>
  );
};
SubProducts.propTypes = {
  //
};
export default SubProducts;
